import { logger } from "@/lib/logger";
import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseIdleTimeoutOptions {
  /** Timeout in milliseconds before showing warning (default: 14 minutes) */
  warningTimeout?: number;
  /** Timeout in milliseconds before auto-logout (default: 15 minutes) */
  logoutTimeout?: number;
  /** Whether the timeout is enabled (default: true when authenticated) */
  enabled?: boolean;
  /** Callback when user is logged out due to inactivity */
  onLogout?: () => void;
}

interface UseIdleTimeoutReturn {
  /** Whether the warning modal should be shown */
  showWarning: boolean;
  /** Seconds remaining before logout */
  secondsRemaining: number;
  /** Reset the idle timer (call when user confirms they're still active) */
  resetTimer: () => void;
  /** Dismiss warning and reset timer */
  dismissWarning: () => void;
}

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'focus'
];

const DEFAULT_WARNING_TIMEOUT = 14 * 60 * 1000; // 14 minutes
const DEFAULT_LOGOUT_TIMEOUT = 15 * 60 * 1000; // 15 minutes

export function useIdleTimeout(options: UseIdleTimeoutOptions = {}): UseIdleTimeoutReturn {
  const {
    warningTimeout = DEFAULT_WARNING_TIMEOUT,
    logoutTimeout = DEFAULT_LOGOUT_TIMEOUT,
    enabled = true,
    onLogout
  } = options;

  const { toast } = useToast();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    
    // Log security event
    try {
      await supabase.rpc('log_security_event', {
        p_action: 'session_timeout',
        p_resource_type: 'auth',
        p_metadata: { reason: 'idle_timeout', last_activity: lastActivityRef.current }
      });
    } catch (e) {
      logger.error('[IdleTimeout] Failed to log security event:', e);
    }

    // Sign out
    await supabase.auth.signOut();
    
    toast({
      title: "Session Expired",
      description: "You have been logged out due to inactivity.",
      variant: "destructive",
    });

    onLogout?.();
  }, [clearAllTimers, onLogout, toast]);

  const startCountdown = useCallback(() => {
    const remaining = Math.ceil((logoutTimeout - warningTimeout) / 1000);
    setSecondsRemaining(remaining);
    
    countdownRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [logoutTimeout, warningTimeout, handleLogout]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    clearAllTimers();
    setShowWarning(false);
    setSecondsRemaining(Math.ceil((logoutTimeout - warningTimeout) / 1000));

    if (!enabled) return;

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, warningTimeout);

    // Set absolute logout timer as backup
    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, logoutTimeout);
  }, [clearAllTimers, enabled, warningTimeout, logoutTimeout, startCountdown, handleLogout]);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    const handleActivity = () => {
      // Only reset if warning is not showing
      if (!showWarning) {
        resetTimer();
      }
    };

    // Add event listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer start
    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [enabled, showWarning, resetTimer, clearAllTimers]);

  return {
    showWarning,
    secondsRemaining,
    resetTimer,
    dismissWarning
  };
}
